'use client'
// =============================================================================
// ControlTab — « Contrôle » : le contexte d'organisation dans le panneau IA (pages /admin/organisations et /admin/membres).
// Même panneau que l'explorateur / éditeur (onglet Fichiers) : on n'ajoute pas un panneau, on ajoute l'onglet pertinent ici.
//
// Les états connecté / hors ligne viennent de la présence RÉELLE (signalements POST /auth/presence, ensemble Redis) — jamais de
// l'existence d'un compte. Source muette → « présence indisponible », jamais « 0 en ligne ». Chaque administrateur ne voit que ce
// que l'API lui permet : la liste de la plateforme (administrateur de plateforme) ou ses propres organisations (administrateur d'organisation).
// =============================================================================
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { HudFrame } from '@/components/hud/HudFrame'
import { HudIcon } from '@/components/hud/HudIcon'
import { usePlatformOrgs } from '@/lib/platform-orgs-store'

interface Me { platformAdmin: boolean }
interface OrgPresence { name: string; available: boolean; online: number; offline: number }

export function ControlTab() {
  const platformOrgs = usePlatformOrgs()
  const [me, setMe] = useState<Me | null>(null)
  const [own, setOwn] = useState<OrgPresence[] | null>(null)
  const [ownCount, setOwnCount] = useState<number | null>(null)

  useEffect(() => {
    fetch('/api/platform/whoami').then(r => (r.ok ? r.json() : null)).then(d => setMe({ platformAdmin: !!d?.platform?.admin })).catch(() => setMe({ platformAdmin: false }))
  }, [])

  // Administrateur d'organisation (pas de plateforme) : la présence de SES organisations, par le plan métier (non tracé).
  useEffect(() => {
    if (!me || me.platformAdmin) return
    let off = false
    ;(async () => {
      try {
        const r = await fetch('/api/orgs'); const d = r.ok ? await r.json() as { organizations?: { id: string; name: string; role: string }[] } : { organizations: [] }
        const mine = (d.organizations ?? []).filter(o => o.role === 'OWNER' || o.role === 'ADMIN')
        if (!off) setOwnCount(mine.length)
        const rows = await Promise.all(mine.map(async o => {
          const p = await fetch(`/api/orgs/${encodeURIComponent(o.id)}/presence`).then(x => (x.ok ? x.json() : null)).catch(() => null) as { available: boolean; online: unknown[]; offline: unknown[] } | null
          return { name: o.name, available: !!p?.available, online: p?.online.length ?? 0, offline: p?.offline.length ?? 0 }
        }))
        if (!off) setOwn(rows)
      } catch { if (!off) { setOwn([]); setOwnCount(0) } }
    })()
    return () => { off = true }
  }, [me])

  // Totaux : jamais mélangés entre sources, et « inconnu » si une organisation n'a pas de présence.
  let orgCount: number | null = null, online: number | null = null, offline: number | null = null
  if (me?.platformAdmin && platformOrgs) {
    orgCount = platformOrgs.length
    if (platformOrgs.every(o => o.presence)) { online = platformOrgs.reduce((s, o) => s + o.presence!.online, 0); offline = platformOrgs.reduce((s, o) => s + o.presence!.offline, 0) }
  } else if (me && !me.platformAdmin && own) {
    orgCount = ownCount
    if (own.every(o => o.available)) { online = own.reduce((s, o) => s + o.online, 0); offline = own.reduce((s, o) => s + o.offline, 0) }
  }
  const loading = me === null || (me.platformAdmin ? platformOrgs === null : own === null)
  const n = (v: number | null) => (v === null ? '—' : String(v))

  return (
    <div className="ctl">
      <HudFrame>
        <Link href="/admin/organisations" className="ctl-item" style={{ textDecoration: 'none' }}>
          <span className="ic"><HudIcon name="organisations" size={26} /></span>
          <div><h4>Organisations</h4><p><span className="n">{loading ? '…' : orgCount === null ? 'indisponible' : `${orgCount} organisation${orgCount > 1 ? 's' : ''}`}</span><br />Organisations accessibles et organisation sélectionnée.</p></div>
          <span aria-hidden>›</span>
        </Link>
      </HudFrame>

      <HudFrame>
        <Link href="/admin/membres" className="ctl-item" style={{ textDecoration: 'none' }}>
          <span className="ic"><HudIcon name="connectes" size={26} /></span>
          <div><h4>Personnes connectées</h4><p><span className="n">{loading ? '…' : online === null ? 'présence indisponible' : `${n(online)} en ligne`}</span><br />Membres actifs qui ont signalé leur présence récemment.</p></div>
          <span className={`ctl-dot ${online === null ? 'na' : 'on'}`} aria-hidden />
        </Link>
      </HudFrame>

      <HudFrame>
        <Link href="/admin/membres" className="ctl-item" style={{ textDecoration: 'none' }}>
          <span className="ic"><HudIcon name="horsligne" size={26} /></span>
          <div><h4>Personnes hors ligne</h4><p><span className="n">{loading ? '…' : offline === null ? 'présence indisponible' : `${n(offline)} hors ligne`}</span><br />Membres actifs sans signalement de présence récent.</p></div>
          <span className={`ctl-dot ${offline === null ? 'na' : 'off'}`} aria-hidden />
        </Link>
      </HudFrame>

      <HudFrame accent="var(--gold, #f5b942)">
        <div className="ctl-item" style={{ cursor: 'default' }}>
          <span className="ic" style={{ color: 'var(--gold, #f5b942)', borderColor: 'rgba(245,185,66,.4)' }}><HudIcon name="administration" size={26} /></span>
          <div>
            <h4>Administration</h4>
            <p>
              <Link href="/admin/membres" style={{ color: 'var(--accent-cyan)' }}>Membres & accès</Link><br />
              {me?.platformAdmin && <><Link href="/admin/organisations" style={{ color: 'var(--accent-cyan)' }}>Administrer les organisations</Link><br /></>}
              <Link href="/parametres/organisation" style={{ color: 'var(--accent-cyan)' }}>Identité de mon organisation</Link>
            </p>
          </div>
        </div>
      </HudFrame>

      {me && !loading && (me.platformAdmin ? platformOrgs === null : own === null) && <div className="ft-note">Liste indisponible pour le moment.</div>}
    </div>
  )
}
